/**
 * ScopePicker — the admin top bar's scope control (founder 2026-10-27: "maybe there is a selected state on click and
 * then a check box to trigger the counties view for that state").
 *
 *   [ California ▾ ]  ☐ Counties                         → every California state-level vote
 *   [ California ▾ ]  ☑ Counties  [ Sacramento County ▾ ] → that county's votes (lit counties of the state only)
 *   [ Federal ▾ ]                                         → US Congress (no checkbox)
 *
 * Scopes are separate slices of jurisdiction, never additive: the state level does not include its counties, a county does not
 * include its state. The first menu is Federal and the states VOTD has counties for; the checkbox switches between
 * the state's own level and its counties; the county menu lists only lit counties ("not live" when not served).
 * No "everything" option. Narrowing inside a county is turned on by a gap-alarm link, or (since 2026-11-01) by Sourced
 * Votes' "part of the county" filter, which offers only the parts with a vote in view; ScopeNarrow shows it either way.
 * Shown only on editor pages, and only when the database has the scope doors.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { useScope, stateName } from '../lib/scope';

/** A small button + menu, closing on outside click / Escape. */
function Menu({ label, children, width = 'w-56' }: { label: string; children: (close: () => void) => ReactNode; width?: string }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', close); document.addEventListener('keydown', esc);
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc); };
  }, [open]);
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen(!open)} aria-haspopup="menu" aria-expanded={open}
              className="inline-flex items-center gap-1 max-w-[14rem] px-2.5 py-1 rounded-lg border border-border bg-white text-xs font-semibold text-near-black hover:bg-surface-light focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300">
        <span className="truncate">{label}</span><ChevronDown size={13} className="shrink-0 text-gray-text" />
      </button>
      {open && (
        <div role="menu" className={`absolute left-0 top-full mt-1 z-40 ${width} max-h-[60vh] overflow-y-auto rounded-lg border border-border bg-white shadow-lg p-1.5`}>
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
function Item({ on, onClick, children, note }: { on: boolean; onClick: () => void; children: ReactNode; note?: string }) {
  return (
    <button role="menuitemradio" aria-checked={on} onClick={onClick}
            className="w-full text-left px-3 py-1.5 text-sm rounded-md hover:bg-surface-light flex items-center gap-2 focus:outline-none focus-visible:bg-surface-light">
      {on ? <Check size={14} className="shrink-0 text-brand-orange" /> : <span className="w-[14px] shrink-0" />}
      <span className="flex-1">{children}</span>
      {note && <span className="text-xs text-amber-700">{note}</span>}
    </button>
  );
}

export function ScopePicker() {
  const { available, scope, setScope, counties } = useScope();
  if (available !== true || !scope) return null;

  const states = Array.from(new Set(counties.map(c => c.state))).sort((a, b) => stateName(a).localeCompare(stateName(b)));
  const litIn = (st: string) => counties.filter(c => c.state === st && c.lit)
    .sort((a, b) => Number(b.served) - Number(a.served) || a.county.localeCompare(b.county));
  const st = scope.level === 'federal' ? null : scope.state;
  const countiesOn = scope.level === 'county';
  const firstCounty = (s: string) => litIn(s)[0];

  return (
    <div className="flex flex-wrap items-center gap-2 min-w-0">
      <Menu label={st ? stateName(st) : 'Federal'}>
        {close => (
          <>
            <Item on={!st} onClick={() => { setScope({ level: 'federal' }); close(); }}>Federal</Item>
            <div className="my-1 border-t border-border" />
            {states.map(s => (
              <Item key={s} on={st === s} onClick={() => {
                // Keep the counties view when switching state, if the new state has a live county.
                const c = countiesOn ? firstCounty(s) : undefined;
                setScope(c ? { level: 'county', state: s, county: c.county_area_id } : { level: 'state', state: s });
                close();
              }}>{stateName(s)}</Item>
            ))}
          </>
        )}
      </Menu>

      {st && (
        <label className={`inline-flex items-center gap-1.5 text-xs font-medium ${litIn(st).length ? 'text-near-black cursor-pointer' : 'text-gray-text'}`}
               title={litIn(st).length ? undefined : `No county in ${stateName(st)} has votes coming in yet`}>
          <input type="checkbox" className="h-3.5 w-3.5 accent-[#C85014]" checked={countiesOn} disabled={!litIn(st).length && !countiesOn}
                 onChange={e => {
                   if (e.target.checked) { const c = firstCounty(st); if (c) setScope({ level: 'county', state: st, county: c.county_area_id }); }
                   else setScope({ level: 'state', state: st });
                 }} />
          Counties
        </label>
      )}

      {scope.level === 'county' && (
        <Menu label={counties.find(c => c.county_area_id === scope.county)?.county ?? 'County'}>
          {close => litIn(scope.state).map(c => (
            <Item key={c.county_area_id} on={scope.county === c.county_area_id} note={c.served ? undefined : 'not live'}
                  onClick={() => { setScope({ level: 'county', state: c.state, county: c.county_area_id }); close(); }}>{c.county}</Item>
          ))}
        </Menu>
      )}
    </div>
  );
}

/** Narrowing shows only when it is on (from a gap-alarm link, or Sourced Votes' "part of the county" filter): "Folsom
 *  only ×". No standing control here — each page keeps its own filters (founder 2026-10-27: "should be simple"); a page
 *  whose filter is the narrowing sets it through setNarrow, so it travels in the link (founder 2026-11-01). */
export function ScopeNarrow() {
  const { ready, scope, narrow, setNarrow, gaps } = useScope();
  if (!ready || !scope || scope.level !== 'county' || !narrow) return null;
  const TYPE: Record<string, string> = { county: 'County board', cities: 'Cities', schools: 'School districts', specials: 'Special districts', media: 'Media' };
  const what = narrow.startsWith('city:') ? (gaps.find(g => g.city_body_id === narrow.slice(5))?.label ?? 'One city') : (TYPE[narrow] ?? narrow);
  return (
    <div className="px-4 md:px-6 pt-3">
      <span className="inline-flex items-center gap-1.5 rounded-full bg-near-black text-white px-2.5 py-1 text-xs font-medium">
        {what} only
        <button aria-label={`Show the whole county, not just ${what}`} className="opacity-80 hover:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-300 rounded" onClick={() => setNarrow(null)}>×</button>
      </span>
    </div>
  );
}
